import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { Modal, Button } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import Search from '@/pages/intelligence/components/SearchRecent/components/Search';
import { addSeachRecord, seachRecordList } from '@/pages/intelligence/services/common';
import { deleteGlobalViewSearchRecord } from '@/pages/intelligence/services/globalView';
import { buriedPointSearch } from '@/pages/home/utils/buriedPoint';
import { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import './index.scss';
import { getLocale } from 'umi';
import { LANGUAGE_CN } from '@/pages/intelligence/_config/config';
import { getSearchKeywordsWithCutTag } from '@intelligence/pages/GenreProfile/utils';

/**
 * 搜索组件，支持历史记录和实时搜索功能
 * @param {object} props
 * @param {string} props.searchType - 搜索类型，用于区分不同页面的搜索
 * @param {boolean} props.disabled - 是否禁用搜索框
 * @param {string} props.dataNameKey - 原始数据中表示搜索词的key值，默认为'name'
 * @param {boolean} props.isOuterSearchKey - 是否不附加searchKey参数
 * @param {string} props.searchKey - 搜索参数key，默认为'search_by_name'
 * @param {Function} props.fetchParams - 获取页面参数的函数
 * @param {boolean} props.isFrontEndSearch - 是否为前端搜索
 * @param {Function} props.searchListApi - 搜索列表接口
 * @param {Function} props.getTableResultList - 表格数据获取接口
 * @param {Function} props.handleClear - 清除搜索内容的回调
 * @param {Function} props.transRecordDataForSearch - 搜索记录数据格式转换函数
 * @param {Function} props.transTableDataForSearch - 表格数据格式转换函数
 * @param {boolean} props.hightLinghtKeyWord - 是否高亮搜索关键字
 * @param {string} props.initSearchInput - 初始搜索输入值
 * @param {string} props.placeholder - 搜索框占位文本
 * @param {boolean} props.isCustomRendering - 是否使用自定义渲染
 * @param {Function} props.getAddRecordParams - 获取添加记录参数
 * @param {Function} props.getDeleteRecordParams - 获取删除记录参数
 * @param {string} props.genreName - 类型名称
 * @param {string} props.dimension - 维度
 * @param {Function} props.loadingDispatch - 加载状态分发函数
 * @returns {JSX.Element} 搜索组件
 */
export default function SearchRecent(props: any) {
  const {
    searchType,
    disabled,
    // 表示原始数据中，用于表示搜索词的key值，默认为name
    dataNameKey = 'name',
    // 默认会附加searchKey，这个参数为true的时候不附加
    isOuterSearchKey,
    searchKey = 'search_by_name',
    fetchParams /** 页面的参数 */,
    isFrontEndSearch,
    /** 搜索接口：展示在搜索框的列表结果的接口 */
    searchListApi = () => console.log,
    getTableResultList /** 表格接口：点击后重新请求表格接口的接口 */,
    handleClear: handleClearProp = () => console.log /** 清除接口 */,
    searchType: searchTypeProp /** 接口用的搜索类型--区分哪个页面 */,
    pageLogInfo /** 搜索打点 */,
    /** 将搜索记录格式转为搜索展示格式 */
    transRecordDataForSearch = () => console.log,
    /** 将表格的格式转为搜索展示格式  */
    transTableDataForSearch = () => console.log,
    hightLinghtKeyWord = true, // 搜索是否高亮关键字
    initSearchInput,
    placeholder,
    isCustomRendering,
    getAddRecordParams,
    getDeleteRecordParams,
    genreName,
    dimension,
    loadingDispatch,
  } = props;

  const locale = getLocale();
  const isCN = useMemo(() => locale === LANGUAGE_CN, [locale]);
  const [loading, setLoading] = useState(false);
  const [recentRecordList, setRecentRecordList] = useState<any[]>([]);
  const [searchResultList, setSearchResultList] = useState<any[]>([]);
  // 搜索请求接口防抖--用最新的那个返回值
  const inputKey = useRef(0);

  /** 搜索历史记录 */
  const getRecentRecord = async () => {
    setLoading(true);
    setRecentRecordList([]);
    setSearchResultList([]);
    try {
      const result = await seachRecordList({
        type: ['companies', 'games'].includes(dimension)
          ? `M${searchType.slice(5)}_${genreName}`
          : searchType,
      });
      /** 对历史记录数据进行处理，并只要前5 TODO--目前后端做*/
      const transList = transRecordDataForSearch(result);
      setRecentRecordList(transList);
    } catch (err) {}
    setLoading(false);
  };

  /** 获取搜索结果列表--展示在搜索框 */
  const getSearchResultList = async (inputValue: string, type: string) => {
    /** 展示状态： Loading \ 数据 \ 空态 */
    setLoading(true);
    return new Promise((resolve, reject) => {
      if (isFrontEndSearch) {
        const transList = transTableDataForSearch(inputValue);
        setLoading(false);
        resolve(transList);
      } else {
        const appendParams = isOuterSearchKey
          ? {}
          : {
              [searchKey]: inputValue,
            };
        searchListApi({
          ...fetchParams(inputValue, type),
          ...appendParams,
          page: 1,
        })
          .then((result: any) => {
            const transList = transTableDataForSearch(result);
            resolve(transList);
            setLoading(false);
          })
          .catch(() => {
            resolve([]);
            setLoading(false);
          });
      }
    });
  };

  /** 点击搜索结果对应条目展示在表格上 */
  const clickSearchItem = async (type: 'record' | 'search', cvm: RecordItemType) => {
    /** 添加条目到历史记录 */
    if (type !== 'record') {
      addRecordItem(cvm);
    }
    if (getTableResultList instanceof Function) {
      if (type !== 'record') {
        getTableResultList(cvm, type);
      } else if (isFrontEndSearch) {
        getTableResultList(cvm, type);
        // 点击历史记录，数据需要从接口重新取
      } else {
        loadingDispatch({ type: 'table', payload: true });
        getSearchResultList(cvm?.[dataNameKey], 'clickRecord')
          .then((result: any) => {
            const apiCurrent = inputKey.current;
            /** input 输入框频繁请求接口，正确的显示最后一次接口返回的数据 */
            if (apiCurrent === inputKey.current) {
              getTableResultList(cvm, type);
            }
          })
          .catch(() => {
            loadingDispatch({ type: 'table', payload: false });
          });
      }
    }
  };

  /** 输入搜索关键值 */
  const handleChangeInput = (inputValue: string) => {
    const inputValueTmp = getSearchKeywordsWithCutTag(locale, inputValue);
    setSearchResultList([]);
    inputKey.current = inputKey.current + 1;
    getSearchResultList(inputValueTmp, 'search').then((result: any) => {
      const apiCurrent = inputKey.current;
      /** input 输入框频繁请求接口，正确的显示最后一次接口返回的数据 */
      if (apiCurrent === inputKey.current) {
        setSearchResultList(result);
      }
    });
    // 添加搜索打点
    if (pageLogInfo?.pageId) {
      buriedPointSearch({ extInfo: pageLogInfo?.pageId, typeId: pageLogInfo?.typeId }, inputValue);
    }
  };

  /** 点X清除 */
  const handleClear = () => {
    /** 重新展示历史记录 */
    getRecentRecord();
    handleClearProp();
  };

  const addRecordItem = (item: any) => {
    if (typeof item !== 'object') {
      return;
    }
    const params = getAddRecordParams(item);
    addSeachRecord({
      add_message: [
        Object.assign(params, {
          type: ['companies', 'games'].includes(dimension)
            ? `${searchType}_${genreName}`
            : searchType,
        }),
      ],
    });
  };

  /** 删除某个历史记录 */
  const deleteRecordItem = async (item: any) => {
    // console.log(item);
    if (typeof item !== 'object') {
      return;
    }
    const params = getDeleteRecordParams(item);
    const result = await deleteGlobalViewSearchRecord(
      Object.assign(params, {
        type: `${searchType}_${genreName}`,
      }),
    );
    if (result.code === 0) {
      getRecentRecord();
    } else {
      Modal.error({
        message: 'Sorry',
        description: 'Data cannot be deleted !',
        buttons: [<Button type="primary">Got it!</Button>],
        size: 'm',
      });
    }
  };

  const renderComp = useMemoizedFn(
    (
      item: any,
      {
        triggerCompClick,
        onClearIcon,
        showClearIcon,
      }: {
        triggerCompClick: Function;
        onClearIcon: Function;
        showClearIcon: boolean;
      },
    ) => {
      const { name, name_cn, icon } = item;
      const nameText = isCN ? name_cn || name : name;
      return (
        <div
          className="dbi-recent-flex dbi-top-genre-market-recent-search-overlay"
          onClick={() => {
            triggerCompClick(item);
          }}
        >
          <div className="recent-left" key={nameText}>
            {icon && <img className="flex-county" src={icon} alt="" width={24} height={16} />}
            <span className="flex-text">{nameText}</span>
          </div>
          {showClearIcon && (
            <div
              className="recent-right"
              onClick={() => {
                if (onClearIcon instanceof Function) {
                  onClearIcon(item);
                }
              }}
            >
              <SvgIcon type="clear-icon" className="recent-icon" />
            </div>
          )}
        </div>
      );
    },
  );

  useEffect(() => {
    if (searchType) {
      getRecentRecord();
    }
  }, [searchType]);

  return (
    <Search
      disabled={disabled}
      loading={loading}
      handleChangeInput={handleChangeInput}
      recentRecordList={recentRecordList}
      searchResultList={searchResultList}
      initSearchInput={initSearchInput}
      placeholder={placeholder}
      handleClear={handleClear}
      clickSearchItem={clickSearchItem}
      deleteRecordItem={deleteRecordItem}
      getRecentRecord={getRecentRecord}
      hightLinghtKeyWord={hightLinghtKeyWord}
      isCustomRendering={isCustomRendering}
      renderComp={renderComp}
      defaultNameStr="name"
      nameStr={isCN ? 'name_cn' : 'name'}
    />
  );
}
